import { addLoaderPlugins, Application, BasePlugin, loadAtlas } from '@goodboydigital/astro';
import { Texture, utils } from 'pixi.js';

import { App } from '../App';
import { loadSpine3x, loadSpine3xAtlas } from '../display/loadSpine3x';
import { loadJsonZip } from '../utils/loadJsonZip';

export class AssetsPlugin implements BasePlugin
{
    public app: App;
    public textures: Record<string, Texture>;
    public data: Record<string, any>;

    public availableFlags: string[];

    constructor(app: Application)
    {
        this.app = app as App;
        this.textures = {};
        this.data = {};
        this.availableFlags = [];
        loadAtlas.test = () => false;
        addLoaderPlugins([loadJsonZip, loadSpine3x, loadSpine3xAtlas]);
    }

    public init(): void
    {
        this.app.resources.manager.onLoadComplete.connect(() => { this.refresh(); });
    }

    /** Update internal register */
    private refresh(): void
    {
        this.availableFlags = [];

        // Register textures by their simple names, removing extensions
        for (const f in utils.TextureCache)
        {
            const texture = utils.TextureCache[f];
            const plainName = removeUrlParams(f);
            const name = shortName(plainName);

            this.textures[name] = texture;

            if (name.startsWith('flag-')) this.availableFlags.push(name.replace('flag-', ''));
        }

        for (const f in this.app.resources.cache)
        {
            const v = this.app.resources.cache[f];

            if (f.endsWith('.json') || f.endsWith('.jsz'))
            {
                const plainName = removeUrlParams(f);
                const name = shortName(plainName);

                this.data[name] = v;
            }
        }

        this.availableFlags.sort();
    }

    public getTexture(name: string): Texture
    {
        if (!this.textures[name]) throw new Error(`Texture not found: ${name}`);

        return this.textures[name];
    }

    public hasTexture(name: string): boolean
    {
        return !!this.textures[name];
    }

    public getData<T>(name: string): T
    {
        if (!this.data[name]) throw new Error(`Data not found: ${name}`);

        return this.data[name] as T;
    }

    public hasData(name: string): boolean
    {
        return !!this.data[name];
    }

    public searchData(match: string|RegExp): string[]
    {
        const findings: string[] = [];

        for (const f in this.data)
        {
            if (f.match(match)) findings.push(f);
        }

        return findings;
    }

    /**
     * Get texture sequence ordered alphabetically
     * @param prefix - Prefix for the names
     * @returns
     */
    public getFrames(prefix: string): Texture[]
    {
        const frames = [];
        const ids = [];

        // Find ids that matches the prefix
        for (const id in this.textures)
        {
            if (id.startsWith(prefix)) ids.push(id);
        }

        // Sort ids alphabetically
        ids.sort();

        // Push textures in 'ids' order
        for (const id of ids)
        {
            frames.push(this.textures[id]);
        }

        return frames;
    }
}

/** Turns "path/to/some-image\@2x.png?cache=1234" into "some-image" */
function shortName(str: string): string
{
    return removeExtension(removeResolution(removePath(str)));
}

/** Remove "?" and everything after that from given string */
function removeUrlParams(str: string): string
{
    return str.split('?').shift() || str;
}

/** Remove "\@2x", "\@1.5x" and things like that from given string */
function removeResolution(str: string): string
{
    return str.replace(/@[\d]*\.?[\d]x/g, '');
}

/** Remove "." and everything after that from given string */
function removeExtension(str: string): string
{
    return str.split('.').shift() || str;
}

/** Remove eventual "/" and everything before from given string */
function removePath(str: string): string
{
    return str.split('/').pop() || str;
}
